import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const JENIS_SELENGGARA = ["Servis Berjadual", "Pembaikan Kerosakan", "Pemeriksaan PUSPAKOM", "Baik Pulih Kemalangan", "Lain-lain"];
const KAEDAH_LUPUS = ["Sebut Harga", "Lelongan", "Jualan Sisa (Skrap)", "Pindahan ke Agensi Lain", "Musnah / E-Waste"];
const SEBAB_LUPUS = ["Tidak ekonomik dibaiki", "Usang / tamat hayat perkhidmatan", "Rosak teruk akibat kemalangan", "Teknologi lapuk", "Lebihan keperluan"];

export function VehicleStatusDialog({ vehicle, open, onOpenChange, onDone }) {
  const [status, setStatus] = useState("penyelenggaraan");
  const [d, setD] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && vehicle) {
      setStatus(vehicle.status === "tersedia" ? "penyelenggaraan" : vehicle.status);
      setD(vehicle.status === "penyelenggaraan" ? vehicle.maintenance_info || {} : vehicle.status === "dilupus" ? vehicle.disposal_info || {} : {});
    }
  }, [open, vehicle]);

  useEffect(() => {
    if (open && status === "penyelenggaraan") {
      setD((x) => (x.odometer ? x : { ...x, odometer: String(vehicle?.current_odometer ?? "") }));
    }
  }, [open, status, vehicle]);

  if (!vehicle) return null;
  const set = (k, v) => setD((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/vehicles/${vehicle.id}/status`, { status, details: status === "tersedia" ? null : d });
      toast.success(`Status ${vehicle.registration_number} ditukar kepada "${status}"`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const field = (label, k, type = "text", ph = "", extra = null) => (
    <div className="space-y-1.5">
      <Label className="flex items-center">{label}{extra}</Label>
      <Input type={type} value={d[k] || ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} data-testid={`status-${k}-input`} />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Tukar Status — {vehicle.registration_number}</DialogTitle>
          <DialogDescription className="text-xs">Rekod butiran mengikut Pekeliling WP 4.1 Pengurusan Kenderaan Kerajaan.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Status Baharu</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="tersedia">Tersedia (aktif semula)</SelectItem>
                <SelectItem value="penyelenggaraan">Penyelenggaraan</SelectItem>
                <SelectItem value="dilupus">Dilupus</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {status === "penyelenggaraan" && (
            <div className="space-y-3 border border-amber-200 bg-amber-50/50 rounded-lg p-3" data-testid="maintenance-fields">
              <button type="button" data-testid="import-engarage-btn"
                className="w-full flex items-center justify-center gap-2 rounded-md bg-[#10056E] hover:bg-[#1a0b8f] transition-colors text-white text-xs font-semibold py-2 px-3">
                Import dari
                <img src="https://customer-assets-jt897jd0.emergentagent.net/job_c11c5258-44d1-4e99-aa9f-de5db0e12288/artifacts/n9scsoqg_ENGARAGE%20Wordmark_White%404x.webp" alt="ENGARAGE" className="h-3.5" />
              </button>
              <p className="text-xs font-bold text-amber-800">REKOD PENYELENGGARAAN BERJADUAL (Pekeliling WP 4.1)</p>
              <div className="space-y-1.5">
                <Label>Jenis Penyelenggaraan</Label>
                <Select value={d.jenis || ""} onValueChange={(v) => set("jenis", v)}>
                  <SelectTrigger data-testid="status-jenis-select"><SelectValue placeholder="Pilih jenis" /></SelectTrigger>
                  <SelectContent className="bg-white">{JENIS_SELENGGARA.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field("Tarikh", "tarikh_masuk", "date")}
                {field("Odometer (km)", "odometer", "number", "", <span className="text-[9px] text-emerald-700 font-bold ml-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">AUTO</span>)}
              </div>
              <div className="space-y-1.5">
                <Label>Butir-butir penyelenggaraan & pemeriksaan berkala (ringkas)</Label>
                <Textarea value={d.butiran || ""} onChange={(e) => set("butiran", e.target.value)} data-testid="status-butiran-input" className="min-h-16 bg-white"
                  placeholder="Cth: Tukar minyak enjin & penapis, semak brek, tayar dan lampu" />
              </div>
              {field("Nama Pegawai Yang Mengesahkan", "pegawai", "text", "Nama penuh pegawai")}
              <div className="border-t border-amber-200 pt-2">
                <p className="text-[11px] font-bold text-amber-800 mb-2">Jadual penyelenggaraan seterusnya</p>
                <div className="grid grid-cols-2 gap-3">
                  {field("Tarikh", "jadual_tarikh", "date")}
                  {field("Odometer (km)", "jadual_odometer", "number", "Cth: +10,000 km")}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {field("Bengkel / Lokasi", "bengkel", "text", "Cth: Bengkel Panel JKR")}
                {field("Anggaran Kos (RM)", "kos", "number", "0.00")}
              </div>
              {field("Jangkaan Siap", "jangkaan_siap", "date")}
            </div>
          )}

          {status === "dilupus" && (
            <div className="space-y-3 border border-slate-300 bg-slate-50 rounded-lg p-3" data-testid="disposal-fields">
              <div className="space-y-1.5">
                <Label>Kaedah Pelupusan</Label>
                <Select value={d.kaedah || ""} onValueChange={(v) => set("kaedah", v)}>
                  <SelectTrigger data-testid="status-kaedah-select"><SelectValue placeholder="Pilih kaedah" /></SelectTrigger>
                  <SelectContent className="bg-white">{KAEDAH_LUPUS.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sebab Pelupusan</Label>
                <Select value={d.sebab || ""} onValueChange={(v) => set("sebab", v)}>
                  <SelectTrigger data-testid="status-sebab-select"><SelectValue placeholder="Pilih sebab" /></SelectTrigger>
                  <SelectContent className="bg-white">{SEBAB_LUPUS.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {field("No. Rujukan Kelulusan (cth: KEW.PA-16)", "rujukan", "text", "KEW.PA-16/2026/001")}
              {field("Tarikh Pelupusan", "tarikh", "date")}
            </div>
          )}

          {status !== "tersedia" && (
            <div className="space-y-1.5">
              <Label>Catatan</Label>
              <Textarea value={d.catatan || ""} onChange={(e) => set("catatan", e.target.value)} data-testid="status-catatan-input" className="min-h-16" />
            </div>
          )}

          <Button className="w-full bg-[#10056E] hover:bg-[#2a17a8]" disabled={busy} onClick={save} data-testid="status-save-btn">
            {busy ? "Menyimpan..." : "Simpan Status"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
